import { DataSource } from '@angular/cdk/collections';
import { formatCurrency, formatDate, getCurrencySymbol } from '@angular/common';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { BehaviorSubject, combineLatest, merge, Observable, Subscription, switchMap } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, tap, withLatestFrom } from 'rxjs/operators';
import {
    LedgerImportDocumentTypeNames,
    LedgerImportDocumentTypes,
} from '../../../dave-data-module/entities/ledger-import.entity';
import {inject} from "@angular/core";
import {
    LedgerImportDataService,
} from "../../../dave-data-module/services/ledger-import-data.service";
import { LedgerImportSelectTableComponentLedgerImportFilter } from './ledger-import-select-table.component';
import {  compareEqualityAsJsonString } from '../../../helper/helper';


export interface LedgerImportSelectTableItem {
    ConsolidatedInvoiceId: string;
    ConsolidatedAmount: string;
    ConsolidatedAmountWithoutTax: string;
    notes: string;
    CommissionId: number;
    isSelected$: Observable<boolean>;
    Id: number;
    DueDate: string;
    Type: string;
    ConsolidatedDate: string;
    CreatedAt: string;

    sortType: string;
    sortCreatedAtTime: number;
    sortConsolidatedDate: number;
    sortDueDate: number;
    sortConsolidatedAmount: number;
    sortConsolidatedAmountWithoutTax: number;
}

/**
 * Data source for the LedgerImportSelectTable view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class LedgerImportSelectTableDataSource extends DataSource<LedgerImportSelectTableItem> {
    // maybe use SelectionModel instead
    protected ledgerImportDataService = inject(LedgerImportDataService);


    filter$ = new BehaviorSubject<LedgerImportSelectTableComponentLedgerImportFilter | null>(null);
    /** document types selected by the user in the filter card, narrows filter$.documentTypes */
    documentTypeFilter$ = new BehaviorSubject<LedgerImportDocumentTypes[]>([]);
    selectedLedgerImports$ = new BehaviorSubject<number[]>([]);
    data$: Observable<LedgerImportSelectTableItem[]> = combineLatest([this.filter$, this.documentTypeFilter$]).pipe(
        map(([filter, documentTypes]): LedgerImportSelectTableComponentLedgerImportFilter | null => {
            if (!documentTypes.length) {
                return filter;
            }
            return {
                customerIds: filter?.customerIds || [],
                commissionIds: filter?.commissionIds || [],
                documentTypes: filter?.documentTypes?.length ? filter.documentTypes.filter((t) => documentTypes.includes(t)) : documentTypes,
            };
        }),
        distinctUntilChanged(compareEqualityAsJsonString),
        tap(() => this.loading$.next(true)),
        switchMap(filter => this.ledgerImportDataService.get$({
            LedgerImportIds: [],
            CommissionIds: (filter?.commissionIds || []).map(n => n.toString()),
            CustomerIds: (filter?.customerIds || []).map(n => n.toString()),
            EventIds: [],
            UserIds: [],
            DocumentTypes: (filter?.documentTypes || []),
            DocumentStatus: [],
            }
        )),
        tap(() => this.loading$.next(false)),
        map((lis) => {
            const currentlySelected = this.selectedLedgerImports$.value;

            if (currentlySelected.some((id) => !lis.some((l) => l.Id === id))) {
                this.selectedLedgerImports$.next(currentlySelected.filter((id) => lis.some((l) => l.Id === id)));
            }
            return lis.map((l) => {
                return {
                    ConsolidatedInvoiceId: l.ConsolidatedInvoiceId,
                    ConsolidatedAmount: formatCurrency(l.ConsolidatedAmount, this.locale, getCurrencySymbol(l.ConsolidatedCurrencyCode, 'narrow', this.locale), l.ConsolidatedCurrencyCode),
                    ConsolidatedAmountWithoutTax: formatCurrency((l.ConsolidatedNettoAmount || 0) / 100, this.locale, getCurrencySymbol(l.ConsolidatedCurrencyCode, 'narrow', this.locale), l.ConsolidatedCurrencyCode),
                    notes: l.AdditionalData?.notes || '',
                    CommissionId: l.CommissionId,
                    Id: l.Id,
                    isSelected$: this.selectedLedgerImports$.pipe(map(selected => selected.includes(l.Id))),
                    DueDate: l.DueDate ? formatDate(l.DueDate, 'shortDate', this.locale) : '',
                    Type: l.DocumentType === LedgerImportDocumentTypes.PartialInvoice ? (l.PartialInvoiceCounter + '. ' + LedgerImportDocumentTypeNames.get(l.DocumentType)) : LedgerImportDocumentTypeNames.get(l.DocumentType),
                    ConsolidatedDate: l.ConsolidatedDate ? formatDate(l.ConsolidatedDate, 'shortDate', this.locale) : '',
                    CreatedAt: l.CreatedAt ? formatDate(l.CreatedAt, 'shortDate', this.locale) : '',
                    sortType: l.DocumentType && LedgerImportDocumentTypeNames.get(l.DocumentType),
                    sortCreatedAtTime: l.CreatedAt.getTime(),
                    sortConsolidatedDate: l.ConsolidatedDate?.getTime(),
                    sortDueDate: l.DueDate?.getTime(),
                    sortConsolidatedAmount: l.ConsolidatedAmount,
                    sortConsolidatedAmountWithoutTax: l.ConsolidatedNettoAmount,
                };
            });
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    paginator: MatPaginator | undefined;
    sort: MatSort | undefined;

    loading$ = new BehaviorSubject<boolean>(false);
    length$ = this.data$.pipe(map(d => d?.length || 0));
    allSelected$ = combineLatest([this.length$, this.selectedLedgerImports$]).pipe(
        map(([dataLength, selected]) => dataLength === selected.length),
        distinctUntilChanged(),
    );
    someSelected$ = combineLatest([this.length$, this.selectedLedgerImports$]).pipe(
        map(([dataLength, selected]) => selected.length > 0 && dataLength !== selected.length),
        distinctUntilChanged(),
    );
    constructor(private locale: string) {
        super();
    }
    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect(): Observable<LedgerImportSelectTableItem[]> {
        if (this.paginator && this.sort) {
            // Combine everything that affects the rendered data into one update
            // stream for the data-table to consume.
            return merge(this.data$, this.paginator.page, this.sort.sortChange).pipe(
                withLatestFrom(this.data$),
                map(([_, data]) => {
                    return this.getPagedData(this.getSortedData([...data]));
                }),
            );
        } else {
            throw Error('Please set the ledgerImportFilterByCommissionId, paginator and sort on the data source before connecting.');
        }
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect(): void {
    }

    /**
     * Paginate the data (client-side). If you're using server-side pagination,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getPagedData(data: LedgerImportSelectTableItem[]): LedgerImportSelectTableItem[] {
        if (this.paginator) {
            const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
            return data.splice(startIndex, this.paginator.pageSize);
        } else {
            return data;
        }
    }

    /**
     * Sort the data (client-side). If you're using server-side sorting,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getSortedData(data: LedgerImportSelectTableItem[]): LedgerImportSelectTableItem[] {
        if (!this.sort || !this.sort.active || this.sort.direction === '') {
            return data.sort((a, b) => b.Id - a.Id);
        }

        return data.sort((a, b) => {
            const isAsc = this.sort?.direction === 'asc';
            switch (this.sort?.active as keyof LedgerImportSelectTableItem) {
                case "ConsolidatedAmountWithoutTax":
                    return compare(a.sortConsolidatedAmountWithoutTax, b.sortConsolidatedAmountWithoutTax, isAsc);
                case "DueDate":
                    return compare(a.sortDueDate, b.sortDueDate, isAsc);
                case "Type":
                    return compare(a.sortType, b.sortType, isAsc);
                case "ConsolidatedDate":
                    return compare(a.sortConsolidatedDate, b.sortConsolidatedDate, isAsc);
                case "CreatedAt":
                    return compare(a.sortCreatedAtTime, b.sortCreatedAtTime, isAsc);
                case 'ConsolidatedInvoiceId':
                    return compare((a.ConsolidatedInvoiceId || '').toLowerCase(), (b.ConsolidatedInvoiceId || '').toLowerCase(), isAsc);
                case 'ConsolidatedAmount':
                    return compare(a.sortConsolidatedAmount, b.sortConsolidatedAmount, isAsc);
                case 'notes':
                    return compare((a.notes || '').toLowerCase(), (b.notes || '').toLowerCase(), isAsc);
                case 'CommissionId':
                case "Id":
                case "isSelected$":
                    console.error('not sortable: ' + this.sort?.active);
                    return 0;
                default:
                    return 0;
            }
        });
    }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
